/*
 * Rennsieger — shared front-end behaviour for rsg-* sections.
 *
 * Ported from the landing mockup, with keyboard operability, focus trapping,
 * scroll locking and reduced-motion guards added (build spec §2.5, §3.2).
 * No dependencies. Safe to load on every page; each initialiser no-ops when
 * its markup is absent.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ *
   * Mobile drawer
   * ------------------------------------------------------------------ */
  function initDrawer() {
    var drawer = document.querySelector('[data-rsg-drawer]');
    var openBtn = document.querySelector('[data-rsg-drawer-open]');
    var closeBtn = document.querySelector('[data-rsg-drawer-close]');
    if (!drawer || !openBtn) return;

    // init() re-runs on shopify:section:load; without this the listeners below
    // would stack up and a click would toggle the drawer more than once.
    if (drawer.dataset.rsgDrawerBound === '1') return;
    drawer.dataset.rsgDrawerBound = '1';

    // Portal the drawer to <body>.
    // It ships inside the header section group, whose wrapper creates its own
    // stacking context — so the hero lockup (painted later in the document)
    // rendered ON TOP of the open drawer regardless of how high we set
    // z-index. Reparenting to body escapes that context entirely. Idempotent,
    // so re-running on shopify:section:load is safe.
    if (drawer.parentElement !== document.body) {
      document.body.appendChild(drawer);
    }

    var lastFocused = null;

    function isOpen() {
      return drawer.classList.contains('is-open');
    }

    // Push the drawer's content clear of the header. Measured rather than
    // hardcoded: the announcement bar sits above the header and shifts its
    // offset, and the header is sticky so that offset changes on scroll.
    function syncTop() {
      var nav = document.querySelector('.rsg-nav');
      if (!nav) return;
      var bottom = Math.max(0, nav.getBoundingClientRect().bottom);
      drawer.style.setProperty('--rsg-drawer-top', Math.round(bottom) + 'px');
    }

    // The single hamburger lives in the header, outside the drawer, so it has
    // to be part of the focus ring or keyboard users could never reach the
    // control that closes the panel.
    function focusables() {
      var items = Array.prototype.slice.call(
        drawer.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter(function (el) { return el.offsetParent !== null; });
      return [openBtn].concat(items);
    }

    function open() {
      lastFocused = document.activeElement;
      syncTop();
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      openBtn.classList.add('is-open');          // folds the bars into an X
      openBtn.setAttribute('aria-expanded', 'true');
      // Lock background scroll while the drawer owns the screen.
      document.documentElement.style.overflow = 'hidden';
      // Focus the first nav link rather than the toggle the user just pressed.
      var items = focusables();
      if (items[1]) items[1].focus();
    }

    function close(returnFocus) {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      openBtn.classList.remove('is-open');
      openBtn.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
      if (returnFocus === false) return;
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    // Burger is the single toggle: opens and closes.
    openBtn.addEventListener('click', function () {
      if (isOpen()) { close(); } else { open(); }
    });

    // Optional: supported if a close control is ever added inside the drawer.
    if (closeBtn) closeBtn.addEventListener('click', function () { close(); });

    // Keep the offset correct if the viewport changes while the panel is open.
    window.addEventListener('resize', function () { if (isOpen()) syncTop(); });
    window.addEventListener('orientationchange', function () { if (isOpen()) syncTop(); });

    // Close on link tap so navigating away never leaves the drawer stuck open.
    // Focus is not restored here: the browser is about to change page.
    drawer.querySelectorAll('a[href]').forEach(function (a) {
      a.addEventListener('click', function () { close(false); });
    });

    document.addEventListener('keydown', function (e) {
      if (!isOpen()) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }

      if (e.key === 'Tab') {
        var items = focusables();
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * FAQ accordion — one open at a time (§3.9)
   * ------------------------------------------------------------------ */
  function initAccordions() {
    var groups = document.querySelectorAll('[data-rsg-faq]');
    if (!groups.length) return;

    groups.forEach(function (group) {
      var heads = group.querySelectorAll('[data-rsg-faq-head]');

      heads.forEach(function (head) {
        head.addEventListener('click', function () {
          var item = head.closest('[data-rsg-faq-item]');
          var body = item.querySelector('[data-rsg-faq-body]');
          var isOpen = item.classList.contains('is-open');

          // Collapse everything in this group first.
          group.querySelectorAll('[data-rsg-faq-item]').forEach(function (other) {
            other.classList.remove('is-open');
            var otherBody = other.querySelector('[data-rsg-faq-body]');
            var otherHead = other.querySelector('[data-rsg-faq-head]');
            if (otherBody) otherBody.style.maxHeight = null;
            if (otherHead) otherHead.setAttribute('aria-expanded', 'false');
          });

          if (!isOpen) {
            item.classList.add('is-open');
            head.setAttribute('aria-expanded', 'true');
            // scrollHeight gives us a real target for the max-height transition.
            body.style.maxHeight = body.scrollHeight + 'px';
          }
        });
      });

      // Honour an author-set "open by default" item.
      var preOpen = group.querySelector('[data-rsg-faq-item].is-open');
      if (preOpen) {
        var b = preOpen.querySelector('[data-rsg-faq-body]');
        var h = preOpen.querySelector('[data-rsg-faq-head]');
        if (b) b.style.maxHeight = b.scrollHeight + 'px';
        if (h) h.setAttribute('aria-expanded', 'true');
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Scroll reveal
   * ------------------------------------------------------------------ */
  function initReveal() {
    var els = document.querySelectorAll('.rsg-reveal');
    if (!els.length) return;

    // No motion, or no observer support: show everything immediately.
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        obs.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    els.forEach(function (el) { obs.observe(el); });
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------ *
   * Drawer nav groups (Aesthetics / Performance dropdowns)
   * ------------------------------------------------------------------ */
  function initNavGroups() {
    var groups = document.querySelectorAll('[data-rsg-navgroup]');
    if (!groups.length) return;

    Array.prototype.forEach.call(groups, function (group) {
      if (group.dataset.rsgBound === '1') return;
      group.dataset.rsgBound = '1';

      var head = group.querySelector('[data-rsg-navgroup-head]');
      var body = group.querySelector('[data-rsg-navgroup-body]');
      if (!head || !body) return;

      head.addEventListener('click', function () {
        var isOpen = group.classList.contains('is-open');

        // One group open at a time — two expanded lists in a drawer this size
        // pushes the utility pair off screen.
        Array.prototype.forEach.call(groups, function (other) {
          other.classList.remove('is-open');
          var ob = other.querySelector('[data-rsg-navgroup-body]');
          var oh = other.querySelector('[data-rsg-navgroup-head]');
          if (ob) ob.style.maxHeight = null;
          if (oh) oh.setAttribute('aria-expanded', 'false');
        });

        if (!isOpen) {
          group.classList.add('is-open');
          head.setAttribute('aria-expanded', 'true');
          body.style.maxHeight = body.scrollHeight + 'px';
        }
      });
    });
  }

  function init() {
    initDrawer();
    initNavGroups();
    initAccordions();
    initReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Shopify theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', init);
})();
